import {
	normalizeAdData,
	type NormalizedAdData,
	type AdRequest,
	type AdResponse,
	type AdResponseData,
} from 'service/ad/model.ts';
import { Signal } from '@preact/signals';

// Components can use this signal and adjust their behavior based on the signal value
export const adblockDetected = new Signal<boolean | null>(null);
let checkedAdblock = false;

/**
 * Production data comes with invalid JSON data with leading comment tag
 * For example "<!-- spir.czMonS[4_2350366___4474278] -->{...data}<!-- spir.czMonE -->"
 * @param advertResponseData
 * @returns
 */
function separateJson(advertResponseData: string): string {
	const matchedString = advertResponseData.match(/{(?:(?!<\/script>)(?:a|[^a]))*}/);
	return matchedString ? matchedString[0].trim() : advertResponseData;
}

function callbackWrapper(advert: AdResponse, _zoneData: AdRequest, actualCallback: GetAdsParams['callback']) {
	try {
		if (advert.type === 'empty') {
			return;
		} else if (advert.type === 'error') {
			// Don't do anything during error. Keep last loaded ad visible thus hiding any ad loading errors from user.
			console.error('Failed to get advert', advert);
			return;
		}

		const clearedJsonData = separateJson(advert.data.trim());
		const advertData = JSON.parse(clearedJsonData) as AdResponseData;

		const normalizedAdData = normalizeAdData(advertData);

		actualCallback(advert, normalizedAdData);
		if (typeof globalThis.window.sssp?.served === 'function') {
			globalThis.window.sssp.served(advert.tracking.served);
		}
	} catch (e) {
		console.error('Failed to parse advert', e, advert);
	}
}

export type GetAdsParams = Omit<AdRequest, 'callback'> & {
	callback: (advert: AdResponse, advertData: undefined | NormalizedAdData) => void;
};

export function getAds(params: GetAdsParams) {
	// Check if adblock is present. If it is, the signal gets updated and reliant components may update themselves.

	if (!checkedAdblock) {
		checkedAdblock = true;

		// Try the ssp. If adblock is off, we will get 400 but no error is caught.
		// Use the ?test=1 to not mess with production logs
		fetch(`https://ssp.seznam.cz/v1/xhr?test=1`, { method: 'POST' })
			.then(() => {
				adblockDetected.value = false;
			})
			.catch(() => {
				adblockDetected.value = true;
			});
	}

	const sendRequest = () => {
		if (typeof globalThis.window.sssp?.getAds !== 'function') {
			return;
		}

		globalThis.window.sssp.getAds({
			...params,
			callback: (advert: AdResponse, zoneData: AdRequest) => {
				callbackWrapper(advert, zoneData, params.callback);
			},
		});
	};

	// At the same time send an actual request for ads to not waste any time in case adblock is off.
	// We cannot detect any errors raised from sssp.getAds, therefore the code above.
	if (!globalThis.window.sssp) {
		const onSSSPLoaded = () => {
			globalThis.window.removeEventListener('ssploaded', onSSSPLoaded);
			sendRequest();
		};
		// SSP script could be lately loaded
		globalThis.window.addEventListener('ssploaded', onSSSPLoaded);
		return;
	}

	sendRequest();
}
